const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "agri-token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

async function req(path, { method = "GET", body, params } = {}) {
  let url = `${API}${path}`;
  if (params) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    if (qs) url += `?${qs}`;
  }
  const opts = { method, headers: {}, cache: "no-store" };
  const token = getToken();
  if (token) opts.headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }

  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), path.includes("/suggestions/ai") ? 30000 : 6000);
    try {
      const res = await fetch(url, { ...opts, signal: ctrl.signal });
      clearTimeout(timer);
      if (res.status === 401) {
        if (path.startsWith("/auth/")) {
          let detail = "Invalid username or password";
          try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch (e) { /* ignore */ }
          throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }
        clearToken();
        window.location.reload();
        throw new Error("Session expired. Please sign in again.");
      }
      if (!res.ok) {
        let detail = `${res.status} ${res.statusText}`;
        try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch (e) { /* ignore */ }
        throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
      }
      return res.status === 204 ? null : await res.json();
    } catch (e) {
      clearTimeout(timer);
      lastErr = e;
      // Retry only on network/abort errors; surface completed HTTP errors immediately.
      if (e.name !== "AbortError" && e.name !== "TypeError") break;
    }
  }
  throw lastErr;
}

export const api = {
  getSettings: () => req("/settings"),
  updateSettings: (data) => req("/settings", { method: "PUT", body: data }),

  getActivities: (livestock_type) => req("/activities", { params: { livestock_type } }),
  createActivity: (data) => req("/activities", { method: "POST", body: data }),
  deleteActivity: (id) => req(`/activities/${id}`, { method: "DELETE" }),

  getTransactions: (type) => req("/transactions", { params: type ? { type } : undefined }),
  createTransaction: (data) => req("/transactions", { method: "POST", body: data }),
  updateTransaction: (id, data) => req(`/transactions/${id}`, { method: "PUT", body: data }),
  deleteTransaction: (id) => req(`/transactions/${id}`, { method: "DELETE" }),

  getBills: () => req("/bills"),
  createBill: (data) => req("/bills", { method: "POST", body: data }),
  updateBill: (id, data) => req(`/bills/${id}`, { method: "PUT", body: data }),
  deleteBill: (id) => req(`/bills/${id}`, { method: "DELETE" }),

  getLedger: () => req("/ledger"),
  createLedger: (data) => req("/ledger", { method: "POST", body: data }),
  deleteLedger: (id) => req(`/ledger/${id}`, { method: "DELETE" }),

  summary: () => req("/stats/summary"),
  pnl: () => req("/stats/pnl"),
  monthly: () => req("/stats/monthly"),
  balanceSheet: () => req("/stats/balance-sheet"),

  getYields: (livestock_type) => req("/yields", { params: { livestock_type } }),
  createYield: (data) => req("/yields", { method: "POST", body: data }),
  deleteYield: (id) => req(`/yields/${id}`, { method: "DELETE" }),
  postYieldIncome: () => req("/yields/post-income", { method: "POST" }),
  yieldTrend: (livestock_type) => req("/stats/yield-trend", { params: { livestock_type } }),
  seedYields: () => req("/seed-yields", { method: "POST" }),
  seedHistory: () => req("/seed-history", { method: "POST" }),
  monthlyRecords: () => req("/stats/monthly-records"),

  ruleSuggestions: () => req("/suggestions/rules"),
  aiSuggestions: (livestock_type) => req("/suggestions/ai", { method: "POST", body: { livestock_type } }),

  seed: () => req("/seed", { method: "POST" }),

  getRecurring: (flow) => req("/recurring", { params: flow ? { flow } : undefined }),
  createRecurring: (data) => req("/recurring", { method: "POST", body: data }),
  updateRecurring: (id, data) => req(`/recurring/${id}`, { method: "PUT", body: data }),
  deleteRecurring: (id) => req(`/recurring/${id}`, { method: "DELETE" }),
  runRecurring: () => req("/recurring/run-now", { method: "POST" }),

  register: (username, password) => req("/auth/register", { method: "POST", body: { username, password } }),
  login: (username, password) => req("/auth/login", { method: "POST", body: { username, password } }),
  me: () => req("/auth/me"),

  getPlantings: () => req("/plantings"),
  createPlanting: (data) => req("/plantings", { method: "POST", body: data }),
  deletePlanting: (id) => req(`/plantings/${id}`, { method: "DELETE" }),
  harvestPlanting: (id) => req(`/plantings/${id}/harvest`, { method: "POST" }),
  getHerd: (livestock_type) => req("/herd", { params: livestock_type ? { livestock_type } : undefined }),
  createHerd: (data) => req("/herd", { method: "POST", body: data }),
  updateHerd: (id, data) => req(`/herd/${id}`, { method: "PUT", body: data }),
  deleteHerd: (id) => req(`/herd/${id}`, { method: "DELETE" }),
  getCustomCrops: () => req("/custom-crops"),
  saveCustomCrop: (data) => req("/custom-crops", { method: "POST", body: data }),
  deleteCustomCrop: (id) => req(`/custom-crops/${id}`, { method: "DELETE" }),
};
